import { compressImage } from './imageCompress';
import { uploadFileToS3 } from './s3Upload';

/*
 * One entry point for image uploads, so the editor and the cover-image field do
 * not each need to know where images live.
 *
 * Compress, then PUT straight to S3 via a presigned URL issued by the backend, and
 * store the returned URL on the post. The bytes never pass through the API gateway.
 *
 * Note: GET /api/s3/presigned-url requires a Bearer token (the backend's
 * SecurityConfig authenticates /api/s3/**), so uploads need a logged-in session.
 */

/*
 * The exact set S3Service.ALLOWED_CONTENT_TYPES accepts. Anything else is a 400
 * from /api/s3/presigned-url ("Unsupported content type"), so it is checked here
 * to fail with a message naming the file instead of mid-upload.
 *
 * SVG is absent deliberately — it can carry <script>, and neither sanitiser
 * allows it as an image source, so an uploaded one would be dropped downstream.
 *
 * GIF/BMP/TIFF are absent too. compressImage returns the file untouched when it
 * is already under the size budget, so a small GIF would otherwise reach the
 * backend as image/gif and be rejected there. Converting it on the way past is
 * not an option: the canvas re-encode keeps only the first frame, which silently
 * throws away the animation that was the point of using a GIF.
 */
const ALLOWED_TYPES = ['image/webp', 'image/jpeg', 'image/png', 'image/avif'];

export const ACCEPT_ATTR = ALLOWED_TYPES.join(',');

const MAX_INPUT_BYTES = 12 * 1024 * 1024;

/**
 * @param {File} file
 * @param {string} folder destination folder in live mode
 * @returns {Promise<string>} a URL usable directly as an <img src>
 */
export async function uploadImage(file, folder = 'blogs') {
  if (!file.type.startsWith('image/')) {
    throw new Error(`${file.name} is not an image.`);
  }
  if (file.type === 'image/svg+xml') {
    throw new Error('SVG uploads are not supported — use PNG, JPEG, WebP or AVIF.');
  }
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error(`${file.name} is a ${file.type} — use PNG, JPEG, WebP or AVIF.`);
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error(`${file.name} is larger than 12MB.`);
  }

  const compressed = await compressImage(file, 100);

  return uploadFileToS3(compressed, folder);
}

/**
 * Upload several images.
 *
 * Sequential, not Promise.all: compressImage decodes each file onto a canvas, and
 * running ten of those at once on a phone is a reliable way to get the tab killed.
 * A failure on one file does not lose the others — it resolves to null and the
 * caller filters it out.
 *
 * @returns {Promise<Array<string|null>>} URLs in the same order as `files`
 */
export async function uploadImages(files, folder = 'blogs') {
  const urls = [];
  const errors = [];

  for (const file of files) {
    try {
      urls.push(await uploadImage(file, folder));
    } catch (err) {
      urls.push(null);
      errors.push(err.message);
    }
  }

  if (errors.length && !urls.some(Boolean)) throw new Error(errors[0]);
  return urls;
}
